import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Card, Spinner, Badge } from 'react-bootstrap';
import { FaMusic, FaPlay, FaDownload, FaPlus, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as api from '~/Services/Service';
import './styles.css';
import AuthStorage from '~/Utils/AuthStorage';
import { caracteristicas } from '~/constant';

const ModalAudios = ({
  show,
  onHide,
  empreendimentoNome,
  codigoGrupoEmpresa,
}) => {
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [audioAtual, setAudioAtual] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState({});
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const categoria = 'audio';

  useEffect(() => {
    if (show && codigoGrupoEmpresa) {
      carregarAudios();
    }
  }, [show, codigoGrupoEmpresa]);

  const carregarAudios = async () => {
    setLoading(true);
    try {
      const data = await api.SismacListar(categoria, codigoGrupoEmpresa);
      setAudios(data.arquivos || []);
    } catch (error) {
      console.error('Erro ao carregar áudios:', error);
      toast.error(
        (error && error.message) || 'Erro ao carregar áudios. Tente novamente.'
      );
      setAudios([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePararAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (audioAtual && audioAtual.url) {
      window.URL.revokeObjectURL(audioAtual.url);
    }
    setAudioAtual(null);
  };

  const handleReproduzirAudio = async audio => {
    try {
      if (audioAtual) {
        handlePararAudio();
      }

      toast.success(`Abrindo áudio: ${audio.nome}`);
      setDownloading({ [audio.nome_completo]: true });

      const downloadData = await api.SismacDownloadArquivo(
        categoria,
        codigoGrupoEmpresa,
        audio.nome_completo
      );

      const byteCharacters = atob(downloadData.conteudo_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadData.tipo_mime });

      const url = window.URL.createObjectURL(blob);
      setAudioAtual({ url: url, nome: audio.nome });
      setDownloading({ [audio.nome_completo]: false });
    } catch (error) {
      console.error('Erro ao carregar áudio:', error);
      toast.error('Erro ao carregar áudio.');
      setDownloading({ [audio.nome_completo]: false });
    }
  };

  const handleDownloadAudio = async audio => {
    try {
      toast.success(`Download iniciado: ${audio.nome}`);
      setDownloading({ [audio.nome_completo]: true });

      const downloadData = await api.SismacDownloadArquivo(
        categoria,
        codigoGrupoEmpresa,
        audio.nome_completo
      );

      const byteCharacters = atob(downloadData.conteudo_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: downloadData.tipo_mime });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadData.nome_arquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Download concluído!');
      setDownloading({ [audio.nome_completo]: false });
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error('Erro ao fazer download do áudio.');
      setDownloading({ [audio.nome_completo]: false });
    }
  };

  const convertToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

 const handleUploadAudio = async event => {
   const files = event.target.files;
   if (!files || files.length === 0) return;

   setUploading(true);

   try {
     for (let i = 0; i < files.length; i++) {
       const file = files[i];

       if (!file.name.toLowerCase().endsWith('.mp3')) {
         toast.error('Apenas arquivos MP3 são permitidos!');
         continue;
       }

       const formData = new FormData();
       formData.append('file', file);

       await api.SismacUploadArquivo(categoria, codigoGrupoEmpresa, formData);

       toast.success(file.name + ' enviado com sucesso!');
     }

     await carregarAudios();
   } catch (error) {
     console.error('Erro ao fazer upload:', error);
     toast.error('Erro ao fazer upload do áudio.');
   } finally {
     setUploading(false);
     if (fileInputRef.current) {
       fileInputRef.current.value = '';
     }
   }
 };

  const handleExcluirAudio = async audio => {
    try {
      await api.SismacExcluirArquivo(
        categoria,
        codigoGrupoEmpresa,
        audio.nome_completo
      );
      toast.success('Áudio excluído com sucesso!');

      if (audioAtual && audioAtual.nome === audio.nome) {
        handlePararAudio();
      }

      await carregarAudios();
    } catch (error) {
      console.error('Erro ao excluir áudio:', error);
      toast.error('Erro ao excluir áudio.');
    }
  };

  const handleClose = () => {
    handlePararAudio();
    setAudios([]);
    onHide();
  };

  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName="sismac_modal"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ color: '#fff' }}>
          ÁUDIOS - {empreendimentoNome}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" style={{ color: '#0747a6' }} />
            <p className="mt-2" style={{ color: '#0747a6' }}>
              Carregando áudios...
            </p>
          </div>
        ) : (
          <>
            {audioAtual && (
              <div className="sismac_player">
                <div className="sismac_player-header">
                  <h6>
                    <FaPlay />
                    <span>Reproduzindo: {audioAtual.nome}</span>
                  </h6>
                  <FaTimes
                    className="sismac_player-close"
                    onClick={handlePararAudio}
                    title="Fechar player"
                  />
                </div>
                <div className="sismac_player-body">
                  <audio ref={audioRef} controls autoPlay src={audioAtual.url}>
                    Seu navegador não suporta o elemento de áudio.
                  </audio>
                </div>
              </div>
            )}

            <Card className="sismac_card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 d-flex align-items-center text-secondary">
                  <FaMusic className="mr-2" />
                  <span>Áudios disponíveis</span>
                  {audios.length > 0 && (
                    <Badge bg="secondary" className="ms-2">
                      {audios.length}
                    </Badge>
                  )}
                </h6>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".mp3"
                    multiple
                    onChange={handleUploadAudio}
                  />
                  {AuthStorage.temCaracteristica(
                    caracteristicas.GerencialSismac
                  ) && (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleFileInputClick}
                      disabled={uploading}
                    >
                      <FaPlus className="mr-1" />
                      {uploading ? 'Enviando...' : 'Adicionar áudio'}
                    </Button>
                  )}
                </div>
              </Card.Header>

              <Card.Body>
                {audios.length === 0 ? (
                  <div className="sismac_empty">
                    <FaMusic size={40} className="sismac_empty-icon" />
                    <p className="sismac_empty-text">
                      Nenhum áudio encontrado.
                    </p>
                  </div>
                ) : (
                  <div className="sismac_list">
                    {audios.map((audio, index) => (
                      <div key={index} className="sismac_list-item">
                        <div className="sismac_list-item-content">
                          <FaMusic
                            className="sismac_list-item-icon sismac_list-item-icon--primary"
                            size={24}
                          />
                          <div className="sismac_list-item-info">
                            <span
                              className="sismac_list-item-title"
                              onClick={() => handleReproduzirAudio(audio)}
                            >
                              <FaPlay size={12} />
                              {audio.nome}
                            </span>
                            <div className="sismac_list-item-subtitle">
                              {audio.tamanho_formatado} • {audio.extensao}
                            </div>
                          </div>
                        </div>
                        <div className="sismac_list-item-actions">
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleDownloadAudio(audio)}
                          >
                            <FaDownload className="mr-1" />
                            Download
                          </Button>
                          {AuthStorage.temCaracteristica(
                            caracteristicas.GerencialSismac
                          ) && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleExcluirAudio(audio)}
                            >
                              <FaTimes className="mr-1" />
                              Excluir
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAudios;
